/*
 * 模态窗
 * Created by rentao on 16/5/9.
 *
 *
 * example:
 *
 * 组件引入，包括样式
    import {
        Dialog,
        DialogHeader,
        DialogBody,
        DialogFooter,
        DialogFooterButton
    } from '../../components/modalDialog';

 * 定义state
    constructor() {
        this.state = {
            showDialog: false
        }
    }

 * 定义方法
    close(e) {
        this.setState({
            showDialog: false
        })
    }

    cancelButtonClick(e) {
        this.close()
    }

    submitButtonClick(e) {
        // do submit...
        this.close()
    }

 * 在render里调用

    return (
        <Dialog
            show={this.state.showDialog}
            hasMask={true}
            closeOnMaskClick={true}
            className="your-modal-dialog"  // modal-dialog 是一种默认样式，包含文本且居中，包括了以下的 title 的 info 结构
            canClose={true}  // 是否显示关闭按钮
            close={this.close}  // 关闭回调
            width="300"  // 可以不写宽度，默认值为240不带单位认为是px，或者直接写成240px，也可以为80%这样的
        >
            <DialogHeader><div className="title">{title}</div></DialogHeader>
            <DialogBody><div className="info">{info}</div></DialogBody>
            <DialogFooter>
                <DialogFooterButton
                    onClick={this.cancelButtonClick}
                >
                    {cancelButtonLabel}
                </DialogFooterButton>
                <DialogFooterButton
                    onClick={this.submitButtonClick}
                >
                    {submitButtonLabel}
                </DialogFooterButton>
            </DialogFooter>
        </Dialog>
    )

 */

import { $document, $window } from '../../utils/bom';
import React, { Component, createRef, RefObject, PropsWithChildren } from 'react';
import './dialog.scss';
import DialogBody from './dialogBody';
import DialogFooter from './dialogFooter';
import DialogFooterButton from './dialogFooterButton';
import DialogHeader from './dialogHeader';

export interface IDialogProps {
    canAbsolute?: boolean;
    show?: boolean;
    hasMask?: boolean;
    closeOnMaskClick?: boolean;
    canClose?: boolean;
    className?: string;
    width?: string; // 带单位px，rem
    close?: (...args: any[]) => void;
}

interface IDialogState {
    focusHelped: boolean;
}

class Dialog extends Component<PropsWithChildren<IDialogProps>, IDialogState> {
    public dialogMainRef: RefObject<HTMLDivElement>;
    public dialogRef: RefObject<HTMLDivElement>;
    public focusHelperRef: RefObject<HTMLInputElement>;
    public state: IDialogState;

    constructor(props: IDialogProps) {
        super(props);
        this.state = {
            focusHelped: false,
        };

        this.close = this.close.bind(this);
        this.onMaskClick = this.onMaskClick.bind(this);

        this.renderCloseButton = this.renderCloseButton.bind(this);
        this.renderMask = this.renderMask.bind(this);
        this.renderDialog = this.renderDialog.bind(this);

        this.resizeReview = this.resizeReview.bind(this);
        this.setFocusHelper = this.setFocusHelper.bind(this);

        this.dialogMainRef = createRef();
        this.dialogRef = createRef();
        this.focusHelperRef = createRef();
    }

    public resizeReview() {
        const el: HTMLDivElement | null = this.dialogMainRef.current;
        if (!el) {
            return;
        }

        const doc = $document.documentElement || $document.body;
        let top = doc.clientHeight / 2 - el.clientHeight / 2;

        if (top < 20 && this.props.canAbsolute) {
            // 高度大于页面时，使用绝对定位，保证浮层可以滚动
            const rootEl = this.dialogRef.current;
            if (rootEl) {
                top = window.scrollY + 20 - rootEl.getBoundingClientRect().top;
                el.style.position = 'absolute';
                el.style.marginBottom = 20 + 'px';
            }
        } else {
            el.style.position = '';
        }

        const left = '50%';
        const marginLeft = -el.clientWidth / 2;
        el.style.top = top + 'px';
        el.style.left = left;
        el.style.marginLeft = marginLeft + 'px';
    }

    public setFocusHelper() {
        if (this.state.focusHelped) {
            return;
        }

        const el: HTMLDivElement | null = this.dialogMainRef.current;
        if (!el) {
            return;
        }

        // 只在浮层里有输入框时生效，为了防止键盘没有收起时用户继续输入，却输入到原来的输入框
        // 使用一个样式上隐藏的输入框，用来取消页面上其它输入框focus
        if (el.getElementsByTagName('input').length > 1) {
            const focusHelper = this.focusHelperRef.current;
            // TODO 逻辑有待优化, setTimeout可以去掉
            if (focusHelper) {
                setTimeout(() => {
                    focusHelper.focus();
                    focusHelper.blur();
                }, 100);
            }
            this.setState({
                focusHelped: true,
            });
        }
    }

    public componentDidMount() {
        $window.addEventListener('resize', this.resizeReview);
        this.resizeReview();
        this.setFocusHelper();
    }

    public componentDidUpdate() {
        this.resizeReview();
        this.setFocusHelper();
    }

    public componentWillUnmount() {
        $window.removeEventListener('resize', this.resizeReview);
        // BodyScroll.enable();
        // this.setState({
        // focusHelped: false,
        // });
    }

    public close(e: MouseEvent) {
        const close = this.props.close;
        if (close) {
            close(e);
        }
    }

    public renderCloseButton() {
        return this.props.canClose ? (
            <a
                href="javascript:"
                className="btn btn-close"
                onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => this.close(e as unknown as MouseEvent)}
            />
        ) : null;
    }

    public onMaskClick(e: MouseEvent) {
        if (this.props.closeOnMaskClick) {
            this.close(e);
        }
    }

    public renderMask() {
        return this.props.hasMask ? (
            <div
                className="dialog-mask"
                onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
                    this.onMaskClick(e as unknown as MouseEvent)
                }
            />
        ) : null;
    }

    public renderDialog() {
        const style = {} as any;
        if (this.props.width) {
            const width = this.props.width;
            style.width = width;
        }

        // this.props.show ? BodyScroll.disable() : BodyScroll.enable();

        return this.props.show ? (
            <div className="common-header-dialog">
                <div className={`${this.props.className}`} ref={this.dialogRef}>
                    {this.renderMask()}
                    <div className="dialog-main" style={style} ref={this.dialogMainRef}>
                        <input type="text" className="focus-helper" ref={this.focusHelperRef} />
                        {this.renderCloseButton()}
                        {this.props.children}
                    </div>
                </div>
            </div>
        ) : null;
    }

    public render() {
        return this.renderDialog();
    }
}

export { Dialog, DialogHeader, DialogBody, DialogFooter, DialogFooterButton };
